import { useMemo, useState, useEffect } from 'react';
import { useAnalytics } from '@/hooks/useAnalytics';
import { GenericTrackTypes } from '@/types/genericTrackTypes';
import { FlyerType, PAGE_SIZE } from '@/config/constants';
import { schoolCategories } from '@/mock/schoolCategoriesMock';
import { communityCategories } from '@/mock/communityCategoriesMock';
import { useLazyQuery, useReactiveVar } from '@apollo/client';
import {
  flyerboardAllFiltersVar,
  filterFacetsVar,
  flyerboardActiveFiltersVar,
  flyerboardFlyersAmountVar
} from '@/graphql/state';
import { gradesString, ages } from '@/pages/AccountInformation/AccountInformation.constants';
import { useLocation } from 'react-router-dom';
import { GET_ALL_FLYERS_FACETS } from '@/graphql/queries';
import { GetAllFlyersResponse } from '@/graphql/types';
import { ModalFilter } from './ModalFilter';
import { DrawerFilter } from './DrawerFilter';
import { isMobile } from '@/utils/isMobile';

interface FiltersProps {
  trackId: string;
  flyerType: string;
  showFilter: boolean;
  setShowFilter: (showFilter: boolean) => void;
}

type OptionType = {
  value: string;
  description: string;
  amount: number;
  disabled?: boolean;
};

export const INITIAL_FILTERS = {
  grades_age: [],
  types: [],
  format: [],
  audiences: [],
  categories: [],
  grades: [],
  saved: [],
  type: [],
  sort: [],
  when: [],
};

export const FlyerboardFilters: React.FC<FiltersProps> = ({
  trackId,
  flyerType,
  showFilter,
  setShowFilter,
}) => {
  const { search } = useLocation();
  const isMobileDevice = isMobile();
  const searchParams = new URLSearchParams(search);
  const categoryId = searchParams.get('categoryId');
  const flyerboardFlyersAmount = useReactiveVar(flyerboardFlyersAmountVar);
  const [amount, setAmount] = useState<number>(0);
  const [getAllFlyers] = useLazyQuery<GetAllFlyersResponse>(GET_ALL_FLYERS_FACETS)

  const [localFilters, setLocalFilters] = useState<{
    grades_age: string[],
    types: string[],
  }>({
    grades_age: [],
    types: [],
  });
  const [filterSections, setFilterSections] = useState<{
    grades_age: OptionType[],
    types:OptionType[],
  }>({
    grades_age: [],
    types: []
  })
  const filterFacets = useReactiveVar(filterFacetsVar);
  const flyerboardActiveFilters = useReactiveVar(flyerboardActiveFiltersVar);
  const flyerboardAllFilters = useReactiveVar(flyerboardAllFiltersVar);

  const getFacets = async() => {
    if(!flyerboardAllFilters) return;
    try {
      let filters = {...flyerboardAllFilters};
      const { types, grades_age } = localFilters;
      if(types.length > 0) {
        filters = {
          ...filters,
          categories: types,
        }
      }

      if(grades_age.length > 0) {
        filters = {
          ...filters,
          gradesOrAges: grades_age,
        }
      }
      const { data } = await getAllFlyers({
        fetchPolicy: 'network-only',
        variables: {
          input: {
            filters,
            pagination: {
              pageNumber: 1,
              pageSize: PAGE_SIZE
            }
          }
        }
      });
      if(data?.getAllFlyers) {
        const { pagination, facets } = data.getAllFlyers;
        setAmount(pagination.total);
        filterFacetsVar(facets);
      }
    }catch(err){
      console.log(err);
    }
  }

  useEffect(() => {
      getFacets();
  }, [localFilters])

  const getGrades = () => {
    const gradeOptions: OptionType[] = [];
      gradesString.forEach((grade) => {
        const facet = filterFacets.gradesOrAges.find(g => g.key === grade.id)
        let amount: number = 0;
        if(facet) {
          amount = facet.count;
        }
        gradeOptions.push({
          description: grade.description,
          value: grade.id,
          amount,
          disabled: amount === 0
        })
      });

    return gradeOptions;
  }

  const getAges = () => {
    const agesOptions: OptionType[] = [];
      ages.forEach(age => {
        const facet = filterFacets.gradesOrAges.find(g => g.key === age.id)
        let amount: number = 0;
        if(facet) {
          amount = facet.count;
        }
        agesOptions.push({
          description: age.description,
          value: age.id,
          amount,
          disabled: amount === 0
        })
      });

    return agesOptions;
  }

  const getCategories = () => {
    const categories = flyerType === FlyerType.school ? schoolCategories : communityCategories;
    const categoryOptions: OptionType[] = [];
      categories.forEach(category => {
        const facet = filterFacets.categories.find(g => g.key === category.id);
        let amount: number = 0;
        if(facet) {
          amount = facet.count;
        }
        categoryOptions.push({
          description: category.title,
          value: category.id,
          amount,
          disabled: amount === 0
        })
      });

    return categoryOptions;
  }

  const onLoadSections = () => {
    const grades_age = flyerType === FlyerType.school ? getGrades() : getAges();
    const categoryOptions = getCategories();
    setFilterSections({
      grades_age,
      types: categoryId ? [] : categoryOptions,
    })
  }

  useEffect(() => {
    onLoadSections();
    //setDefaultGradesAge();
  }, [filterFacets]);

  useEffect(() => {
    if (showFilter) {
      setLocalFilters(flyerboardActiveFilters)
    }
  }, [showFilter]);

  useEffect(() => {
    setAmount(0);
  }, [flyerType]);

  const { track } = useAnalytics();
  const title = `${flyerType} Filters`;

  const persistFilters = () => {
    const currentDate = new Date().toString();
    localStorage.setItem('filtersUpdateAt', currentDate);
    localStorage.setItem('flyerboardFilters', JSON.stringify(localFilters));
    localStorage.setItem('activeFlyerboard', window.location.href);
  };


  const clearHistorycalFilters = () => {
    localStorage.removeItem('filtersUpdateAt');
    localStorage.removeItem('flyerboardFilters');
    localStorage.removeItem('activeFlyerboard');
  };

  const setDefaultGradesAge = () => {
    const { grades_age } = filterSections;
    if (grades_age.length === 1 && localFilters.grades_age.length === 0) {
      const newFilters = { ...flyerboardActiveFilters };
      newFilters.grades_age.push(grades_age[0].value);
      setLocalFilters(newFilters);
    }
  };

  const handleToggle =
    (filterName: string, value: string, description: string) => () => {
      const index = localFilters[filterName].indexOf(value);
      const newFilters = { ...localFilters };
      track({
        action: GenericTrackTypes.CHECKBOX_SELECTED,
        category: `${trackId}:filter:${filterName}:${
          index === -1 ? 'select' : 'unselect'
        }`,
        label: description,
        value: Number(value),
      });
      if (index === -1) {
        newFilters[filterName].push(value);
      } else {
        newFilters[filterName].splice(index, 1);
      }
      setLocalFilters(newFilters);
    };

  const clearLocalFilters = () => {
    setLocalFilters({
      grades_age: [],
      types: [],
    });
  };

  const onClearFilters = () => {
    track({
      action: GenericTrackTypes.BUTTON_PRESSED,
      category: `${trackId}:clear_filters`,
      label: 'Clear All',
    });
    clearLocalFilters();
    setAmount(0);
    flyerboardActiveFiltersVar({
      grades_age: [],
      types: [],
    });
    clearHistorycalFilters();
  };

  const onCloseDrawer = (drawerState: boolean) => {
    setShowFilter(drawerState);
    clearLocalFilters();
    // clearHistorycalFilters();
  };

  const onShowResults = () => {
    track({
      action: GenericTrackTypes.BUTTON_PRESSED,
      category: `${trackId}:show_results`,
      label: 'Show Results',
    });
    setShowFilter(false);
    persistFilters();
    flyerboardActiveFiltersVar(localFilters);
  };


  const disableClearAllButton = useMemo(() => {
    const { types, grades_age } = localFilters;
    return types.length === 0 && grades_age.length === 0;
  }, [localFilters]);

  const viewResultsLabel = useMemo(() => {
    let flyersAmount = ""
    if(amount > 0) {
      flyersAmount = amount.toString();
    }
    else if(flyerboardFlyersAmount > 0) {
      flyersAmount = flyerboardFlyersAmount.toString();
    }

    return `View${flyersAmount ? ` ${flyersAmount} ` : ' '} results`;
  }, [amount, flyerboardFlyersAmount]);


  const props = {
    isOpen: showFilter,
    title,
    viewResultsLabel,
    onClearFilters,
    onShowResults,
    onClose: onCloseDrawer,
    filterSections,
    localFilters,
    disableClearAllButton,
    flyerType,
    handleToggle,
  }

  return isMobileDevice ? (
    <DrawerFilter {...props} />
    ) : (
  <ModalFilter {...props} />
  );

};
